import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useListMyTrainingCoursesQuery } from '../app/api';
import { useIsMobile } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  amber:     '#b06f00',
};

const STATUS_STYLE = {
  not_started: { bg: 'rgba(88,88,91,0.15)',  color: brand.charcoal, label: 'Not started', icon: 'circle' },
  in_progress: { bg: 'rgba(176,111,0,0.15)', color: brand.amber,    label: 'In progress', icon: 'hourglass-split' },
  passed:      { bg: 'rgba(26,127,78,0.15)', color: brand.green,    label: 'Completed',   icon: 'check-circle-fill' },
  assigned:    { bg: 'rgba(88,88,91,0.15)',  color: brand.charcoal, label: 'Not started', icon: 'circle' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.not_started;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.3rem',
      background: s.bg, color: s.color,
      fontWeight: 700, fontSize: '0.7rem',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      padding: '0.2rem 0.55rem', borderRadius: 999,
    }}>
      <i className={`bi bi-${s.icon}`} />
      {s.label}
    </span>
  );
}
StatusBadge.propTypes = { status: PropTypes.string };

export default function TrainingMyCourses() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { data: courses = [], isLoading } = useListMyTrainingCoursesQuery();

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '1rem' : '1.5rem' }}>

      {/* Header banner */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: isMobile ? '1.25rem 1rem' : '1.75rem 2rem', marginBottom: isMobile ? '1rem' : '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '0.75rem' : '1rem', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: isMobile ? '0.5rem' : '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-mortarboard-fill" style={{ color: brand.goldenrod, fontSize: isMobile ? '1.4rem' : '1.75rem' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: isMobile ? '0.65rem' : '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 4px' }}>
              Required learning
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.15rem' : '1.75rem', lineHeight: 1.2 }}>Training</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
              Watch each video the whole way through, then pass the knowledge check.
            </p>
          </div>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner-border" style={{ color: brand.tealBlue }} />
          </div>
        ) : courses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: brand.charcoal }}>
            <i className="bi bi-mortarboard" style={{ fontSize: '2.5rem', color: brand.softGray, display: 'block', marginBottom: '0.5rem' }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No trainings are available right now.</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem' }}>When a new training class is published it will show up here.</p>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {courses.map(c => (
              <li
                key={c.id}
                onClick={() => navigate(`/training/${c.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/training/${c.id}`); } }}
                role="button"
                tabIndex={0}
                style={{
                  padding: isMobile ? '0.85rem 1rem' : '1rem 1.25rem',
                  borderBottom: `1px solid ${brand.softGray}`,
                  borderLeft: c.is_sandbox ? `4px solid ${brand.goldenrod}` : '4px solid transparent',
                  background: c.is_sandbox ? 'rgba(208,155,44,0.06)' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.85rem',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                }}
              >
                <div style={{ background: 'rgba(0,105,166,0.1)', color: brand.tealBlue, width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="bi bi-play-btn" style={{ fontSize: '1.25rem' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.95rem' }}>{c.title}</span>
                    {c.is_sandbox && (
                      <span title="Preview only — this course is in sandbox" style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                        background: 'rgba(208,155,44,0.18)', color: brand.goldenrod,
                        fontWeight: 700, fontSize: '0.65rem',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                        padding: '0.15rem 0.5rem', borderRadius: 999,
                      }}>
                        <i className="bi bi-flask" /> Sandbox
                      </span>
                    )}
                    <StatusBadge status={c.status} />
                  </div>
                  {c.description && (
                    <p style={{ margin: 0, fontSize: '0.825rem', color: brand.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.description}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', color: brand.charcoal, fontSize: '0.8rem', flexShrink: 0 }}>
                  <span title="Modules"><i className="bi bi-collection-play me-1" />{c.module_count || 0}</span>
                  <i className="bi bi-chevron-right" style={{ color: brand.charcoal }} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
